import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { RestService } from '../../../services/rest.service';
import { StorageService } from '../../../services/storage.service';
import { PaymentCodeService } from '../../../services/payment-code.service';
import { GtmAnalyticsService } from '../../../services/gtm-analytics.service';
import { TranslationService } from '../../../services/translation.service';
import { ErrorReportingService } from '../../../services/error-reporting.service';
import { getCurrencySymbol } from '../../../admin/currencies';
import { fmtStoreAmount, priceNum, round2, savePct, storePair, webAnnualTotal } from '../../../payment/price-display';
import {
  StoreBillingService,
  StorePlan,
  StoreProduct,
} from '../../../services/store-billing.service';
import { BrandService } from '../../../../services/brand-config.service';

/**
 * Consumer paywall v2 as a scrollable bottom-sheet (locked-flow spec §4b,
 * markable_paywall_variants_he_v2). Replaces the plain 2-row results popup at
 * every results trigger. Two variants:
 *   A — first check: a free "see my results" CTA stays (never blocking) + upsell.
 *   B — returning (2+): no free path, a quiet exit link back to the initial review.
 * The cards (one-time 30-day pass + subscription with an annual/monthly selector,
 * "הכי נבחר" badge, saving pill, inline billing-clarity) mirror the approved v2
 * design; copy uses the approved terminology (הסקירה הראשונית / התמונה המלאה).
 */
@Component({
  selector: 'app-paywall-sheet',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './paywall-sheet.component.html',
  styleUrl: './paywall-sheet.component.scss',
})
export class PaywallSheetComponent implements OnInit {
  /** A = first check (free CTA stays); B = returning (full gate). */
  @Input() variant: 'A' | 'B' = 'B';
  /** Scan context so the one-time pass / subscription flow has a scanId. */
  @Input() scanId = '';
  /** Free "see my results" CTA tapped (variant A) / exit link (variant B) / close. */
  @Output() close = new EventEmitter<void>();
  /** Native store purchase completed and the server granted access. */
  @Output() purchased = new EventEmitter<void>();

  currencySymbol = '₪';
  oneTimePrice = '';
  annualPrice = '';
  monthlyPrice = '';
  annualSavingLabel = '';
  pricesLoaded = false;
  selectedBilling: 'annual' | 'monthly' = 'annual';

  isProcessing = false;
  processingPlanId: string | null = null;

  // Native apps sell through the App Store / Google Play (store billing); the
  // web checkout is never shown inside the apps.
  storeMode = false;
  storeProducts: Partial<Record<StorePlan, StoreProduct>> = {};
  storeMessage = '';
  restoring = false;

  constructor(
    private router: Router,
    private restService: RestService,
    private storageService: StorageService,
    private paymentCodeService: PaymentCodeService,
    private gtmAnalytics: GtmAnalyticsService,
    public translationService: TranslationService,
    private reporter: ErrorReportingService,
    public store: StoreBillingService,
    public brandService: BrandService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.store.enabled) {
      this.storeMode = true;
      try {
        this.storeProducts = await this.store.loadProducts();
      } catch (e) {
        this.reporter.report(e, 'paywall-sheet.store');
      }
      if (!this.hasStoreProducts) {
        this.storeMessage = this.translationService.translate('payment.store.unavailable');
      } else if (!this.storeProducts.annual && this.storeProducts.monthly) {
        this.selectedBilling = 'monthly';
      }
      this.gtmAnalytics.trackPricingViewed();
      this.pricesLoaded = true;
      return;
    }
    try {
      await this.paymentCodeService.saveCodeAndFetchPrice(
        undefined,
        this.scanId,
      );
    } catch {}
    let priceData = this.storageService.getPriceData();
    if (!priceData) {
      try {
        priceData = await this.restService.request<any>({
          path: 'payment/decode-price',
          method: 'GET',
          queryParams: { ...(this.scanId ? { scanId: this.scanId } : {}), lang: this.translationService.getCurrentLanguage() },
        });
        if (priceData) this.storageService.setPriceData(priceData);
      } catch (e) {
        this.reporter.report(e, 'paywall-sheet');
      }
    }
    if (!priceData) priceData = { currency: 'ILS' } as any;

    this.currencySymbol = getCurrencySymbol(priceData.currency);
    // Price must always be a real admin price — 0/empty is never launch-ready
    // (N57-10 "0₪ bug"), so a non-positive or blank value falls through to the
    // next source and finally a safe non-zero default.
    this.oneTimePrice = this.firstPrice([priceData.oneTimePrice, priceData.price], '');
    this.annualPrice = this.firstPrice([priceData.annualPrice], '');
    this.monthlyPrice = this.firstPrice([priceData.monthlyPrice], '');
    this.annualSavingLabel = (priceData.annualSavingLabel || '').trim();
    this.gtmAnalytics.trackPricingViewed();
    this.pricesLoaded = true;
  }

  /** First candidate that is a real positive price; else the fallback. Prevents
   *  a 0/empty admin value from ever rendering as "0₪" (N57-10). */
  private firstPrice(cands: any[], fallback: string): string {
    for (const c of cands) {
      const n = Number(c);
      if (Number.isFinite(n) && n > 0) return String(c);
    }
    return fallback;
  }

  get subPrice(): string {
    return this.selectedBilling === 'annual'
      ? this.annualPrice
      : this.monthlyPrice;
  }
  get subBillingKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.annualBilling'
      : 'payment.subscription.monthlyBilling';
  }
  get subButtonKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.annualButton'
      : 'payment.subscription.monthlyButton';
  }

  setBilling(billing: 'annual' | 'monthly'): void {
    this.selectedBilling = billing;
  }

  get hasStoreProducts(): boolean {
    return !!(this.storeProducts.onetime || this.storeProducts.monthly || this.storeProducts.annual);
  }

  /** "₪14.90" in LTR, "14.90 ₪" in Hebrew; empty when there is no real price. */
  money(v: unknown): string {
    const n = priceNum(v);
    if (n === null) return '';
    const amount = n.toFixed(2);
    return this.translationService.isRTL()
      ? `${amount} ${this.currencySymbol}`
      : `${this.currencySymbol}${amount}`;
  }

  /** Amount actually charged per cycle for the selected subscription. */
  get subPrimary(): string {
    if (this.storeMode) return this.storeProducts[this.selectedBilling]?.price || '';
    return this.selectedBilling === 'annual'
      ? this.money(webAnnualTotal(priceNum(this.annualPrice)))
      : this.money(this.monthlyPrice);
  }
  get subPerKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.perYear'
      : 'payment.subscription.perMonth';
  }

  /** Annual plan's monthly equivalent, from the same platform and currency. */
  get annualEquivalent(): string {
    let price = '';
    if (this.storeMode) {
      const a = this.storeProducts.annual;
      const y = priceNum(a?.priceAmount);
      if (a && y) price = fmtStoreAmount(round2(y / 12), a.currency, this.translationService.isRTL());
    } else {
      price = this.money(this.annualPrice);
    }
    return price
      ? this.translationService.translate('payment.subscription.annualEquivalent', { price })
      : '';
  }

  /** Whole-percent annual saving; null (badge hidden) when not reliably known. */
  get annualSavePct(): number | null {
    if (this.storeMode) {
      const pair = storePair(this.storeProducts.annual, this.storeProducts.monthly);
      return pair ? savePct(pair.yearly, pair.monthly) : null;
    }
    return savePct(webAnnualTotal(priceNum(this.annualPrice)), priceNum(this.monthlyPrice));
  }

  get oneTimeDisplay(): string {
    return this.storeMode ? this.storeProducts.onetime?.price || '' : this.money(this.oneTimePrice);
  }

  get storeName(): string {
    return this.translationService.translate(
      this.store.isIOS ? 'payment.store.appStore' : 'payment.store.googlePlay',
    );
  }

  async storeBuy(plan: StorePlan): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.processingPlanId = plan;
    this.storeMessage = '';
    try {
      const result = await this.store.buy(plan);
      if (result === 'paid') {
        this.purchased.emit();
        return;
      }
      if (result !== 'cancelled') {
        const key =
          result === 'pending'
            ? 'payment.store.pending'
            : result === 'other_account'
              ? 'payment.store.otherAccount'
              : 'payment.store.failed';
        this.storeMessage = this.translationService.translate(key);
      }
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }

  async storeRestore(): Promise<void> {
    if (this.isProcessing || this.restoring) return;
    this.restoring = true;
    this.isProcessing = true;
    this.storeMessage = '';
    try {
      const paid = await this.store.restore(true);
      if (paid) {
        this.purchased.emit();
        return;
      }
      this.storeMessage = this.translationService.translate('payment.store.restoreNone');
    } finally {
      this.restoring = false;
      this.isProcessing = false;
    }
  }

  /** Free path (variant A) / dignified exit (variant B): just close the sheet. */
  dismiss(): void {
    if (this.isProcessing) return;
    this.close.emit();
  }

  // One-time 30-day pass — a Checkout Order (planType 'onetime'); reuses the
  // same endpoint the full payment screen calls.
  async selectOneTime(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.processingPlanId = 'onetime';
    this.reporter.logEvent('paywall.order.start', { planId: 'onetime' });
    try {
      const response: any = await this.restService.request({
        path: 'payment/create-paypal-order',
        method: 'POST',
        body: {
          scanId: this.scanId,
          planType: 'onetime',
          origin: window.location.origin,
          lang: this.translationService.getCurrentLanguage(),
        },
      });
      if (response?.error?.message === 'Order already paid') {
        this.router.navigate(['/app/results', this.scanId]);
        return;
      }
      if (response?.approvalUrl) {
        window.location.href = response.approvalUrl;
      } else {
        this.reporter.report('paywall order: no approval URL', 'paywall-sheet');
        alert(this.translationService.translate('priceUi.payPalOrderFailed'));
      }
    } catch (e) {
      this.reporter.report(e, 'paywall-sheet');
      alert(this.translationService.translate('priceUi.genericError'));
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }

  // Recurring subscription (annual/monthly) — create a PayPal subscription and
  // redirect to its approval URL.
  async selectSubscription(): Promise<void> {
    if (this.isProcessing) return;
    const planType = this.selectedBilling;
    this.isProcessing = true;
    this.processingPlanId = planType;
    this.reporter.logEvent('paywall.subscription.start', { planType });
    try {
      const response: any = await this.restService.request({
        path: 'payment/create-subscription',
        method: 'POST',
        body: {
          planType,
          origin: window.location.origin,
          lang: this.translationService.getCurrentLanguage(),
          scanId: this.scanId || undefined,
        },
      });
      if (response?.approvalUrl) {
        window.location.href = response.approvalUrl;
      } else {
        this.reporter.report('paywall sub: no approval URL', 'paywall-sheet');
        alert(this.translationService.translate('priceUi.payPalOrderFailed'));
      }
    } catch (e) {
      this.reporter.report(e, 'paywall-sheet');
      alert(this.translationService.translate('priceUi.genericError'));
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }
}
